import { cn } from "@/lib/utils";
import { StagingBanner } from "./StagingBanner";
import { NavSidebar, type NavItem } from "./NavSidebar";
import { MobileNavChips } from "./MobileNavChips";
import { Breadcrumbs, type Crumb } from "./Breadcrumbs";
import { SkipLink } from "@/components/shell/SkipLink";
import { isStaging } from '@/lib/env';

export interface PageShellProps {
  showStaging?: boolean;
  topBar?: React.ReactNode;
  nav?: {
    items: NavItem[];
    activeKey?: string;
    onSelect?: (key: string) => void;
    sidebarHeader?: React.ReactNode;
    sidebarFooter?: React.ReactNode;
  };
  breadcrumbs?: Crumb[];
  pageTitle?: React.ReactNode;
  pageSubtitle?: React.ReactNode;
  pageActions?: React.ReactNode;
  children: React.ReactNode;
  floating?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  /** Removes the sidebar even on desktop (for login/test-pack pages) */
  noSidebar?: boolean;
}

export function PageShell({
  showStaging = isStaging,
  topBar,
  nav,
  breadcrumbs,
  pageTitle,
  pageSubtitle,
  pageActions,
  children,
  floating,
  className,
  contentClassName,
  noSidebar,
}: PageShellProps) {
  return (
    <div className={cn("min-h-screen bg-muted/40 flex flex-col", className)}>
      <SkipLink />
      {showStaging && <StagingBanner />}
      {topBar}

      <div className="container pt-4 pb-32 sm:pb-28 lg:pb-10 flex-1 w-full">
        <div className={cn("flex gap-6", noSidebar && "justify-center")}>
          {!noSidebar && nav && (
            <NavSidebar
              items={nav.items}
              activeKey={nav.activeKey}
              onSelect={nav.onSelect}
              header={nav.sidebarHeader}
              footer={nav.sidebarFooter}
            />
          )}

          <main id="main-content" tabIndex={-1} className={cn("min-w-0 flex-1 outline-none", contentClassName)}>
            {!noSidebar && nav && (
              <div className="mb-4">
                <MobileNavChips
                  items={nav.items}
                  activeKey={nav.activeKey}
                  onSelect={nav.onSelect}
                />
              </div>
            )}

            {breadcrumbs && breadcrumbs.length > 0 && (
              <Breadcrumbs items={breadcrumbs} className="mb-3" />
            )}

            {(pageTitle || pageActions) && (
              <div className="mb-4 sm:mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                  {pageTitle && (
                    <h2 className="h-section text-foreground leading-tight">
                      {pageTitle}
                    </h2>
                  )}
                  {pageSubtitle && (
                    <p className="mt-1 text-sm text-muted-foreground">{pageSubtitle}</p>
                  )}
                </div>
                {pageActions && (
                  <div className="flex flex-wrap items-center gap-2">{pageActions}</div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-5">{children}</div>
          </main>
        </div>
      </div>

      {floating}
    </div>
  );
}
